import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import { CircularProgress } from "@mui/material";

import {
  deleteDepotInstructionMedia,
  getDepotInstructions,
  saveDepotInstructionsText,
  uploadDepotInstructionMedia,
} from "../../../_redux/depots/depotsCrud";
import "../../../../../../_metronic/_assets/sass/components/_site-instructions-section.scss";

const IMAGE_SLOTS = ["image_1", "image_2", "image_3", "image_4", "image_5"];
const VIDEO_SLOTS = ["video_1", "video_2"];
const PDF_SLOT = "pdf";
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_VIDEO_BYTES = 50 * 1024 * 1024;
const MAX_PDF_BYTES = 10 * 1024 * 1024;

/** @param {File} file @returns {string} Lowercase extension without dot. */
function fileExtension(file) {
  const name = file?.name || "";
  const dot = name.lastIndexOf(".");
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : "";
}

const IMAGE_EXT = new Set([
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "bmp",
  "heic",
  "heif",
]);
const VIDEO_EXT = new Set([
  "mp4",
  "webm",
  "mov",
  "mpeg",
  "mpg",
  "m4v",
  "avi",
]);

/**
 * Derives a display filename from a media URL.
 *
 * @param {string} url - Absolute or relative URL.
 * @returns {string} Last path segment or fallback label.
 */
function fileLabelFromUrl(url) {
  if (!url || typeof url !== "string") return "File";
  try {
    const base =
      url.startsWith("http") || url.startsWith("//")
        ? undefined
        : import.meta.env.REACT_APP_SITE_URL || window.location.origin;
    const u = new URL(url, base);
    const parts = u.pathname.split("/").filter(Boolean);
    const seg = parts.pop();
    return seg ? decodeURIComponent(seg) : "File";
  } catch {
    return "File";
  }
}

/**
 * Normalizes legacy depot instruction payloads into text, what3words, and media slots.
 *
 * @param {object} raw - Response `data` object.
 * @returns {{ text: string, what3words: string, slots: Record<string, string> }} Parsed instructions.
 */
function normalizeDepotInstructionsData(raw) {
  if (!raw || typeof raw !== "object") {
    return { text: "", what3words: "", slots: {} };
  }
  const text =
    typeof raw.instructions === "string"
      ? raw.instructions
      : typeof raw.instruction === "string"
      ? raw.instruction
      : "";
  const what3wordsValue = Object.prototype.hasOwnProperty.call(
    raw,
    "what3words"
  )
    ? raw.what3words
    : raw.passphrase;
  const what3words =
    what3wordsValue === null || what3wordsValue === undefined
      ? ""
      : String(what3wordsValue);
  const slots = {};

  if (Object.prototype.hasOwnProperty.call(raw, "pdf_url")) {
    if (typeof raw.pdf_url === "string" && raw.pdf_url) {
      slots[PDF_SLOT] = raw.pdf_url;
    }
  }

  if (raw.images && typeof raw.images === "object" && !Array.isArray(raw.images)) {
    IMAGE_SLOTS.forEach((k) => {
      const u = raw.images[k];
      if (typeof u === "string" && u) slots[k] = u;
    });
  }
  if (raw.videos && typeof raw.videos === "object" && !Array.isArray(raw.videos)) {
    VIDEO_SLOTS.forEach((k) => {
      const u = raw.videos[k];
      if (typeof u === "string" && u) slots[k] = u;
    });
  }

  const slotKeys = [PDF_SLOT, ...IMAGE_SLOTS, ...VIDEO_SLOTS];
  slotKeys.forEach((k) => {
    if (slots[k]) return;
    const v = raw[k];
    if (v && typeof v === "string") slots[k] = v;
  });
  if (raw.urls && typeof raw.urls === "object") {
    slotKeys.forEach((k) => {
      if (slots[k]) return;
      if (raw.urls[k] && typeof raw.urls[k] === "string") slots[k] = raw.urls[k];
    });
  }
  if (raw.media && typeof raw.media === "object") {
    slotKeys.forEach((k) => {
      if (slots[k]) return;
      if (raw.media[k] && typeof raw.media[k] === "string") {
        slots[k] = raw.media[k];
      }
    });
  }

  if (!Object.prototype.hasOwnProperty.call(raw, "pdf_url")) {
    const v = raw[PDF_SLOT];
    if (v && typeof v === "string" && !slots[PDF_SLOT]) slots[PDF_SLOT] = v;
  }

  return { text, what3words, slots };
}

/**
 * Parses axios depot instructions response.
 *
 * @param {object} res - Axios response object.
 * @returns {{ text: string, what3words: string, slots: Record<string, string> }} Parsed instructions.
 */
function parseDepotInstructionsResponse(res) {
  const raw = res?.data?.data ?? res?.data ?? {};
  return normalizeDepotInstructionsData(raw);
}

/**
 * Whether normalized slot map contains at least one URL to apply to UI state.
 *
 * @param {Record<string, string>|undefined|null} slots - Normalized slot URLs.
 * @returns {boolean} True when `setSlotUrls` should run from this payload.
 */
function hasNormalizedMediaSlots(slots) {
  return Boolean(
    slots && typeof slots === "object" && Object.keys(slots).length > 0
  );
}

/**
 * Applies normalized depot instruction `data` to React state.
 *
 * @param {object} raw - Response `data` object.
 * @param {object} setters - State setters `{ setInstructionsDraft, setWhat3wordsDraft, setSlotUrls }`.
 * @returns {boolean} True when `setSlotUrls` was applied; false if callers should refetch.
 */
function applyDepotInstructionsDataToState(raw, setters) {
  const { setInstructionsDraft, setWhat3wordsDraft, setSlotUrls } = setters;
  const normalized = normalizeDepotInstructionsData(raw);
  setInstructionsDraft(normalized.text);
  setWhat3wordsDraft(normalized.what3words);
  if (hasNormalizedMediaSlots(normalized.slots)) {
    setSlotUrls(normalized.slots);
    return true;
  }
  return false;
}

/**
 * Validates file size and MIME type before upload.
 *
 * @param {string} slot - Instruction slot key.
 * @param {File} file - Selected file.
 * @returns {string|null} Error message or null if valid.
 */
function validateInstructionFile(slot, file) {
  if (!file) return "No file selected.";
  const ext = fileExtension(file);
  if (slot === PDF_SLOT) {
    if (file.type && file.type !== "application/pdf") {
      return "Please choose a PDF file.";
    }
    if (!file.type && ext && ext !== "pdf") {
      return "Please choose a PDF file.";
    }
    if (file.size > MAX_PDF_BYTES) {
      return "PDF must be 10 MB or smaller.";
    }
    return null;
  }
  if (slot.startsWith("image_")) {
    const typeOk = file.type?.startsWith("image/");
    const extOk = ext && IMAGE_EXT.has(ext);
    if (file.type && !typeOk) {
      return "Please choose an image file.";
    }
    if (!file.type && ext && !extOk) {
      return "Please choose an image file.";
    }
    if (!file.type && !ext) {
      return "Please choose an image file.";
    }
    if (file.size > MAX_IMAGE_BYTES) {
      return "Image must be 5 MB or smaller.";
    }
    return null;
  }
  if (slot.startsWith("video_")) {
    const typeOk = file.type?.startsWith("video/");
    const extOk = ext && VIDEO_EXT.has(ext);
    if (file.type && !typeOk) {
      return "Please choose a video file.";
    }
    if (!file.type && ext && !extOk) {
      return "Please choose a video file.";
    }
    if (!file.type && !ext) {
      return "Please choose a video file.";
    }
    if (file.size > MAX_VIDEO_BYTES) {
      return "Video must be 50 MB or smaller.";
    }
    return null;
  }
  return "Unknown slot.";
}

/**
 * Depot instructions: written guidance plus fixed PDF / image / video slots.
 * Keeps the legacy no-floor depot API behavior, with `what3words` replacing passphrase.
 *
 * @param {object} props - Component props.
 * @param {string|number} props.entityId - Depot id (required when section is shown).
 * @param {function} props.setSnack - Snackbar setter `{ call, type, message }`.
 * @returns {JSX.Element} Instructions subsection for the depot side panel.
 */
export function DepotInstructionsSection({ entityId, setSnack }) {
  const [loading, setLoading] = useState(true);
  const [instructionsDraft, setInstructionsDraft] = useState("");
  const [what3wordsDraft, setWhat3wordsDraft] = useState("");
  const [slotUrls, setSlotUrls] = useState({});
  const [saveTextLoading, setSaveTextLoading] = useState(false);
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const [deletingSlot, setDeletingSlot] = useState(null);
  const [preview, setPreview] = useState({ open: false, kind: null, src: null });
  const [deleteConfirmSlot, setDeleteConfirmSlot] = useState(null);

  const idSafe = String(entityId ?? "").replace(/[^\w.-]/g, "_");
  const fieldIdPrefix = `depot-si-${idSafe}`;
  const textareaId = `${fieldIdPrefix}-textarea`;
  const what3wordsId = `${fieldIdPrefix}-what3words`;
  const fileInputId = (slot) => `${fieldIdPrefix}-file-${slot}`;
  const pdfReplaceId = `${fieldIdPrefix}-pdf-replace`;
  const pdfEmptyId = `${fieldIdPrefix}-pdf-empty`;

  const loadSeqRef = useRef(0);
  const aliveRef = useRef(true);
  const entityRef = useRef(entityId);

  useEffect(() => {
    entityRef.current = entityId;
  }, [entityId]);

  useEffect(() => {
    setDeleteConfirmSlot(null);
    setSaveTextLoading(false);
    setUploadingSlot(null);
    setDeletingSlot(null);
    setPreview({ open: false, kind: null, src: null });
  }, [entityId]);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      loadSeqRef.current += 1;
    };
  }, []);

  /**
   * Whether it is safe to apply results from async depot instruction work.
   *
   * @param {string|number} forEntity - Depot id at the time async work started.
   * @returns {boolean} Whether the component is still on the same depot.
   */
  const scopeMatches = useCallback((forEntity) => {
    return aliveRef.current && entityRef.current === forEntity;
  }, []);

  const notify = useCallback(
    (type, message) => {
      setSnack({ call: true, type, message });
    },
    [setSnack]
  );

  const loadInstructions = useCallback(
    async (options = {}) => {
      const { quiet } = options;
      if (!entityId) return;
      const forEntity = entityId;
      const runId = ++loadSeqRef.current;
      if (!quiet) {
        setLoading(true);
        setInstructionsDraft("");
        setWhat3wordsDraft("");
        setSlotUrls({});
      }
      try {
        const res = await getDepotInstructions(entityId);
        if (runId !== loadSeqRef.current || !scopeMatches(forEntity)) {
          return;
        }
        const { text, what3words, slots } = parseDepotInstructionsResponse(res);
        setInstructionsDraft(text);
        setWhat3wordsDraft(what3words);
        setSlotUrls(slots);
      } catch (e) {
        if (runId !== loadSeqRef.current || !scopeMatches(forEntity)) {
          return;
        }
        const msg =
          e?.response?.data?.message ||
          e?.message ||
          "Could not load instructions.";
        if (!quiet) {
          notify("error", msg);
          setInstructionsDraft("");
          setWhat3wordsDraft("");
          setSlotUrls({});
        } else {
          notify("error", "Could not refresh instructions.");
        }
      } finally {
        if (
          runId === loadSeqRef.current &&
          !quiet &&
          scopeMatches(forEntity)
        ) {
          setLoading(false);
        }
      }
    },
    [entityId, notify, scopeMatches]
  );

  useEffect(() => {
    loadInstructions();
  }, [loadInstructions]);

  const handleSaveText = () => {
    const forEntity = entityId;
    setSaveTextLoading(true);
    saveDepotInstructionsText(entityId, instructionsDraft, what3wordsDraft)
      .then((res) => {
        if (!scopeMatches(forEntity)) return;
        notify("success", res.data?.message || "Instructions saved.");
        const raw = res?.data?.data;
        if (raw && typeof raw === "object") {
          const slotsSynced = applyDepotInstructionsDataToState(raw, {
            setInstructionsDraft,
            setWhat3wordsDraft,
            setSlotUrls,
          });
          if (!slotsSynced) {
            return loadInstructions({ quiet: true });
          }
        } else {
          return loadInstructions({ quiet: true });
        }
      })
      .catch((e) => {
        if (!scopeMatches(forEntity)) return;
        notify(
          "error",
          e?.response?.data?.message || "Failed to save instructions."
        );
      })
      .finally(() => {
        if (!scopeMatches(forEntity)) return;
        setSaveTextLoading(false);
      });
  };

  const handleFileChosen = (slot, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const err = validateInstructionFile(slot, file);
    if (err) {
      notify("error", err);
      return;
    }
    const forEntity = entityId;
    setUploadingSlot(slot);
    uploadDepotInstructionMedia(entityId, slot, file)
      .then((res) => {
        if (!scopeMatches(forEntity)) return;
        notify("success", res.data?.message || "Upload complete.");
        const raw = res?.data?.data;
        if (raw && typeof raw === "object") {
          const slotsSynced = applyDepotInstructionsDataToState(raw, {
            setInstructionsDraft,
            setWhat3wordsDraft,
            setSlotUrls,
          });
          if (!slotsSynced) {
            return loadInstructions({ quiet: true });
          }
        } else {
          return loadInstructions({ quiet: true });
        }
      })
      .catch((e) => {
        if (!scopeMatches(forEntity)) return;
        notify("error", e?.response?.data?.message || "Upload failed.");
      })
      .finally(() => {
        if (!scopeMatches(forEntity)) return;
        setUploadingSlot(null);
      });
  };

  const runDelete = () => {
    const slot = deleteConfirmSlot;
    if (!slot) return;
    const forEntity = entityId;
    setDeletingSlot(slot);
    deleteDepotInstructionMedia(entityId, slot)
      .then((res) => {
        if (!scopeMatches(forEntity)) return;
        notify("success", res.data?.message || "Removed.");
        setDeleteConfirmSlot(null);
        const raw = res?.data?.data;
        if (raw && typeof raw === "object") {
          const slotsSynced = applyDepotInstructionsDataToState(raw, {
            setInstructionsDraft,
            setWhat3wordsDraft,
            setSlotUrls,
          });
          if (!slotsSynced) {
            return loadInstructions({ quiet: true });
          }
        } else {
          return loadInstructions({ quiet: true });
        }
      })
      .catch((e) => {
        if (!scopeMatches(forEntity)) return;
        notify("error", e?.response?.data?.message || "Delete failed.");
      })
      .finally(() => {
        if (!scopeMatches(forEntity)) return;
        setDeletingSlot(null);
      });
  };

  const imageUsed = IMAGE_SLOTS.filter((s) => slotUrls[s]).length;

  const openPreview = (kind, src) => {
    setPreview({ open: true, kind, src });
  };

  /**
   * Activates a media card on Enter or Space (ARIA button keyboard support).
   *
   * @param {React.KeyboardEvent} e - Key event from the card.
   * @param {() => void} activate - Handler to run (e.g. open preview).
   */
  const handleMediaCardKeyDown = (e, activate) => {
    if (e.key === "Enter" || e.key === " " || e.code === "Space") {
      e.preventDefault();
      activate();
    }
  };

  if (!entityId) return null;

  return (
    <div
      className="site-instructions-section"
      data-instructions-target="depot"
      data-instructions-entity-id={String(entityId)}
    >
      <h2 className="si-section-title">Depot Instructions</h2>
      <p className="si-section-subtitle">
        Provide guidance for accessing the depot, operating locks, and other
        on-site information.
      </p>

      {loading ? (
        <div className="text-center py-5">
          <CircularProgress size={28} />
        </div>
      ) : (
        <>
          <label className="si-label" htmlFor={textareaId}>
            Written instructions
          </label>
          <textarea
            id={textareaId}
            className="form-control si-textarea"
            placeholder="e.g. Enter via the south gate. The keypad code is rotated weekly - call the manager 30 minutes before arrival to confirm."
            value={instructionsDraft}
            onChange={(e) => setInstructionsDraft(e.target.value)}
            rows={5}
          />
          <label className="si-label mt-3" htmlFor={what3wordsId}>
            what3words
          </label>
          <input
            id={what3wordsId}
            type="text"
            className="form-control"
            autoComplete="off"
            placeholder="Enter what3words for this depot"
            value={what3wordsDraft}
            onChange={(e) => setWhat3wordsDraft(e.target.value)}
          />
          <div className="si-text-actions">
            <button
              type="button"
              className="btn btn-dark btn-sm"
              disabled={saveTextLoading}
              onClick={handleSaveText}
            >
              {saveTextLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                "Save instructions"
              )}
            </button>
          </div>

          <div className="si-media-block">
            <div className="si-media-header">
              <span className="si-media-label">Images</span>
              <span className="si-media-count">
                {imageUsed} of {IMAGE_SLOTS.length} slots used
              </span>
            </div>
            <div className="si-slot-grid">
              {IMAGE_SLOTS.map((slot, idx) => {
                const url = slotUrls[slot];
                const busy = uploadingSlot === slot || deletingSlot === slot;
                return (
                  <div key={slot}>
                    {url ? (
                      <div
                        className="si-slot si-filled"
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) =>
                          handleMediaCardKeyDown(e, () =>
                            openPreview("image", url)
                          )
                        }
                        onClick={() => openPreview("image", url)}
                      >
                        <span className="si-slot-number">{idx + 1}</span>
                        <img src={url} alt="" />
                        <div className="si-slot-overlay">
                          <button
                            type="button"
                            className="si-slot-action"
                            aria-label="View image"
                            onClick={(e) => {
                              e.stopPropagation();
                              openPreview("image", url);
                            }}
                          >
                            <VisibilityIcon style={{ fontSize: 16 }} />
                          </button>
                          <button
                            type="button"
                            className="si-slot-action si-delete"
                            aria-label="Delete image"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteConfirmSlot(slot);
                            }}
                          >
                            <DeleteIcon style={{ fontSize: 16 }} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="si-slot si-empty mb-0"
                          disabled={busy}
                          aria-label={`Upload image slot ${idx + 1}`}
                          onClick={() =>
                            document.getElementById(fileInputId(slot))?.click()
                          }
                        >
                          {busy ? (
                            <CircularProgress size={24} />
                          ) : (
                            <>
                              <span className="si-plus">+</span>
                              <span className="si-slot-label">
                                Image {idx + 1}
                              </span>
                            </>
                          )}
                        </button>
                        <input
                          id={fileInputId(slot)}
                          type="file"
                          className="si-file-input"
                          tabIndex={-1}
                          accept="image/*"
                          disabled={busy}
                          aria-hidden={true}
                          onChange={(e) => handleFileChosen(slot, e)}
                        />
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="si-media-block">
            <div className="si-media-header">
              <span className="si-media-label">Videos</span>
              <span className="si-media-count">
                {VIDEO_SLOTS.filter((s) => slotUrls[s]).length} of{" "}
                {VIDEO_SLOTS.length} slots used
              </span>
            </div>
            <div className="si-slot-grid si-videos">
              {VIDEO_SLOTS.map((slot, idx) => {
                const url = slotUrls[slot];
                const busy = uploadingSlot === slot || deletingSlot === slot;
                return (
                  <div key={slot}>
                    {url ? (
                      <div
                        className="si-slot si-filled si-video"
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) =>
                          handleMediaCardKeyDown(e, () =>
                            openPreview("video", url)
                          )
                        }
                        onClick={() => openPreview("video", url)}
                      >
                        <span className="si-slot-number">{idx + 1}</span>
                        <div className="si-video-thumb">
                          <div className="si-video-thumb-icon" />
                        </div>
                        <div className="si-slot-overlay">
                          <button
                            type="button"
                            className="si-slot-action"
                            aria-label="Play video"
                            onClick={(e) => {
                              e.stopPropagation();
                              openPreview("video", url);
                            }}
                          >
                            <VisibilityIcon style={{ fontSize: 16 }} />
                          </button>
                          <button
                            type="button"
                            className="si-slot-action si-delete"
                            aria-label="Delete video"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteConfirmSlot(slot);
                            }}
                          >
                            <DeleteIcon style={{ fontSize: 16 }} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="si-slot si-empty si-video mb-0"
                          disabled={busy}
                          aria-label={`Upload video slot ${idx + 1}`}
                          onClick={() =>
                            document.getElementById(fileInputId(slot))?.click()
                          }
                        >
                          {busy ? (
                            <CircularProgress size={24} />
                          ) : (
                            <>
                              <span className="si-plus">+</span>
                              <span className="si-slot-label">
                                Video {idx + 1}
                              </span>
                            </>
                          )}
                        </button>
                        <input
                          id={fileInputId(slot)}
                          type="file"
                          className="si-file-input"
                          tabIndex={-1}
                          accept="video/*"
                          disabled={busy}
                          aria-hidden={true}
                          onChange={(e) => handleFileChosen(slot, e)}
                        />
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="si-media-block">
            <div className="si-media-header">
              <span className="si-media-label">PDF Document</span>
            </div>
            {slotUrls[PDF_SLOT] ? (
              <div className="si-pdf-slot">
                <div className="si-pdf-icon">PDF</div>
                <div className="si-pdf-info">
                  <div className="si-pdf-name">
                    {fileLabelFromUrl(slotUrls[PDF_SLOT])}
                  </div>
                  <div className="si-pdf-meta">Uploaded document</div>
                </div>
                <div className="si-pdf-actions">
                  <button
                    type="button"
                    className="si-icon-btn"
                    onClick={() => openPreview("pdf", slotUrls[PDF_SLOT])}
                  >
                    View
                  </button>
                  <button
                    type="button"
                    className="si-icon-btn mb-0"
                    aria-label="Replace PDF document"
                    disabled={uploadingSlot === PDF_SLOT}
                    onClick={() =>
                      document.getElementById(pdfReplaceId)?.click()
                    }
                  >
                    Replace
                  </button>
                  <input
                    id={pdfReplaceId}
                    type="file"
                    className="si-file-input"
                    tabIndex={-1}
                    accept="application/pdf"
                    disabled={uploadingSlot === PDF_SLOT}
                    aria-hidden={true}
                    onChange={(e) => handleFileChosen(PDF_SLOT, e)}
                  />
                  <button
                    type="button"
                    className="si-icon-btn si-danger"
                    onClick={() => setDeleteConfirmSlot(PDF_SLOT)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  className={`si-pdf-slot si-empty mb-0${
                    uploadingSlot === PDF_SLOT ? " align-items-center" : ""
                  }`}
                  disabled={uploadingSlot === PDF_SLOT}
                  aria-label="Upload PDF document, max 10 megabytes"
                  onClick={() =>
                    document.getElementById(pdfEmptyId)?.click()
                  }
                >
                  <div className="si-pdf-icon">PDF</div>
                  <div className="si-pdf-info">
                    {uploadingSlot === PDF_SLOT ? (
                      <CircularProgress size={24} />
                    ) : (
                      <>
                        <div className="si-pdf-name" style={{ color: "#6b7280" }}>
                          No PDF uploaded
                        </div>
                        <div className="si-pdf-meta">
                          Click to upload (max 10 MB)
                        </div>
                      </>
                    )}
                  </div>
                </button>
                <input
                  id={pdfEmptyId}
                  type="file"
                  className="si-file-input"
                  tabIndex={-1}
                  accept="application/pdf"
                  disabled={uploadingSlot === PDF_SLOT}
                  aria-hidden={true}
                  onChange={(e) => handleFileChosen(PDF_SLOT, e)}
                />
              </>
            )}
          </div>
        </>
      )}

      <Modal
        show={preview.open}
        onHide={() => setPreview({ open: false, kind: null, src: null })}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {preview.kind === "image" ? (
            <img
              src={preview.src}
              alt=""
              style={{ maxWidth: "100%", maxHeight: "70vh" }}
            />
          ) : null}
          {preview.kind === "video" ? (
            <video controls style={{ width: "100%", maxHeight: "70vh" }}>
              <source src={preview.src} />
              Your browser does not support the video tag.
            </video>
          ) : null}
          {preview.kind === "pdf" ? (
            <iframe
              src={preview.src}
              title="PDF preview"
              style={{ width: "100%", height: "70vh", border: 0 }}
            />
          ) : null}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-end border-top">
          <button
            type="button"
            className="btn btn-dark"
            onClick={() => setPreview({ open: false, kind: null, src: null })}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={Boolean(deleteConfirmSlot)}
        onHide={() => setDeleteConfirmSlot(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete this item?</Modal.Title>
        </Modal.Header>
        <Modal.Body>This action cannot be undone.</Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setDeleteConfirmSlot(null)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={Boolean(deletingSlot)}
            onClick={runDelete}
          >
            {deletingSlot ? <CircularProgress size={18} color="inherit" /> : "Delete"}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default DepotInstructionsSection;
